import type React from "react";
import { Box, Typography, Container, CircularProgress } from "@mui/material";
import testimonialpatterns from "../../../assets/testimonialspattern.png";
import muzukurublack from "../../../assets/muzukurublack.png";
import { useEffect, useState } from "react";
import apiService from "../../../service/Api";

interface Testimonial {
  id: number;
  author_name: string;
  author_description: string;
  company_name: string | null;
  rating: number;
  content: string;
  display_order: number;
}

interface TestimonialsPageProps {
  selectedTestimonialIds?: number[];
}

const TestimonialsPage: React.FC<TestimonialsPageProps> = ({ 
  selectedTestimonialIds = [] 
}) => {
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await apiService.get<Testimonial[]>(
          "/api/configurations/testimonials/",
        );
        
        let fetchedTestimonials = response.data || [];
        
        if (selectedTestimonialIds.length > 0) {
          fetchedTestimonials = fetchedTestimonials.filter(t => 
            selectedTestimonialIds.includes(t.id)
          );
        } else {
          fetchedTestimonials = fetchedTestimonials.slice(0, 3);
        }
        
        setTestimonials(fetchedTestimonials);
      } catch (error) {
        console.error("Failed to fetch testimonials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, [selectedTestimonialIds]);

  return (
    <Box
      id="testimonial-page"
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${testimonialpatterns})`,
        backgroundRepeat: "repeat",
        backgroundSize: "auto",
        py: 13,
        px: 6,
      }}
    >
      <Container maxWidth="md">
        {/* Title */}
        <Typography
          variant="h1"
          align="center"
          sx={{
            letterSpacing: 4,
            color: "#7a7a6f",
            mb: 6,
            fontWeight: 800,
          }}
        >
          TESTIMONIALS
        </Typography>

        {/* Testimonial Cards */}
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 8 }}>
            <CircularProgress size={32} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading testimonials...
            </Typography>
          </Box>
        ) : testimonials.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: "center", mt: 6 }}>
            No testimonials available.
          </Typography>
        ) : (
          testimonials.map((item, index) => (
            <Box
              key={index}
              sx={{
                backgroundColor: "#faf9f6",
                borderRadius: 6,
                px: { xs: 3, md: 6 },
                py: 4,
                mb: 4,
                boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  mb: 3,
                  color: "#2e2e2e",
                }}
              >
                {item.content}
              </Typography>

              {/* Signature */}
              <Typography
                sx={{
                  fontFamily: "Brittany, cursive",
                  fontSize: 28,
                  color: "#2e2e2e",
                }}
              >
                {item.author_name}
              </Typography>
            </Box>
          ))
        )}

        {/* Footer */}
        <Box sx={{ textAlign: "center", mt: 8 }}>
          <img
            src={muzukurublack}
            alt="Muzukuru logo"
            style={{ width: 200, height: 80 }}
          />

          <Typography variant="caption" display="block">
            Phone: +263 776 402 402 | tawanda@muzukuru.com | www.muzukuru.co.zw
          </Typography>
          <Typography variant="caption" display="block">
            46B Mount Pleasant Drive, Mount Pleasant, Harare, Zimbabwe
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialsPage;
